import type { components } from "./schema";

export type Schemas = components["schemas"];
export type MeDto = Schemas["MeDto"];
export type OverviewDto = Schemas["OverviewDto"];
export type OccurrenceDto = Schemas["OccurrenceDto"];
export type OccurrenceListDto = Schemas["OccurrenceListDto"];
export type CalendarDto = Schemas["CalendarDto"];
export type EventDto = Schemas["EventDto"];
export type CreateEventOp = Schemas["CreateEventOp"];
export type UpdateEventOp = Schemas["UpdateEventOp"];
export type ShoppingListSnapshotDto = Schemas["ShoppingListSnapshotDto"];
export type ShoppingItemDto = Schemas["ShoppingItemDto"];
export type StoreSectionDto = Schemas["StoreSectionDto"];
export type ShoppingCommandDto = Schemas["ShoppingCommandDto"];
export type ShoppingCommandsResult = Schemas["ShoppingCommandsResult"];
export type InputStatusDto = Schemas["InputStatusDto"];
export type InputAcceptedDto = Schemas["InputAcceptedDto"];
export type PlanDto = Schemas["PlanDto"];
export type PlanOperationDto = Schemas["PlanOperationDto"];
export type PersonDto = Schemas["PersonDto"];
export type DeviceDto = Schemas["DeviceDto"];
export type NotificationSettingsDto = Schemas["NotificationSettingsDto"];
export type PairingCodeDto = Schemas["PairingCodeDto"];
export type DisplaySnapshotDto = Schemas["DisplaySnapshotDto"];
export type ChangeFeedDto = Schemas["ChangeFeedDto"];
export type UploadSlotDto = Schemas["UploadSlotDto"];
export type AssetDto = Schemas["AssetDto"];
export type FamilySummaryDto = Schemas["FamilySummaryDto"];
export type PlanCommitResult = Schemas["PlanCommitResult"];

/** Fehler im stabilen ProblemDetails-Format des Servers (code, traceId, retryable). */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly retryable: boolean,
    public readonly traceId?: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }

  get isOffline(): boolean {
    return this.status === 0;
  }
}

let csrfToken: string | null = null;

export async function refreshCsrf(): Promise<void> {
  const res = await fetch("/api/v1/auth/csrf", { credentials: "same-origin" });
  if (res.ok) {
    csrfToken = ((await res.json()) as { token: string }).token;
  }
}

/** CSRF-Header für eigene fetch-Aufrufe (z. B. SDP-Austausch des Sprachassistenten). */
export async function csrfHeader(): Promise<Record<string, string>> {
  if (!csrfToken) await refreshCsrf();
  return csrfToken ? { "X-CSRF-TOKEN": csrfToken } : {};
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  raw?: BodyInit;
  signal?: AbortSignal;
};

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? (options.body !== undefined || options.raw !== undefined ? "POST" : "GET");
  const headers: Record<string, string> = { Accept: "application/json", ...options.headers };
  if (method !== "GET" && method !== "HEAD") {
    if (!csrfToken) {
      await refreshCsrf();
    }
    if (csrfToken) {
      headers["X-CSRF-TOKEN"] = csrfToken;
    }
  }
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      credentials: "same-origin",
      body: options.raw ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
      signal: options.signal,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiError(0, "offline", "Keine Verbindung zum Server.", true);
  }

  if (res.status === 400 && method !== "GET") {
    const problem = await res.clone().json().catch(() => null);
    if (problem?.code === "csrf_invalid" && !options.headers?.["x-retried"]) {
      await refreshCsrf();
      return api<T>(path, { ...options, headers: { ...options.headers, "x-retried": "1" } });
    }
  }

  if (!res.ok) {
    const problem = await res.json().catch(() => ({}));
    throw new ApiError(
      res.status,
      problem.code ?? (res.status === 401 ? "unauthenticated" : "http_" + res.status),
      problem.title ?? "Unerwarteter Fehler.",
      problem.retryable ?? res.status >= 500,
      problem.traceId,
      problem.details,
    );
  }
  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined as T;
  }
  return (await res.json()) as T;
}

/** Stabile ID je Nutzeraktion – bei Wiederholungen identisch verwenden (Idempotenz). */
export function newRequestId(prefix = "pwa"): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

export function idempotent(key: string): Record<string, string> {
  return { "Idempotency-Key": key };
}

export function ifMatch(version: number | string): Record<string, string> {
  return { "If-Match": `"${version}"` };
}

/** Upload mit Fortschrittsanzeige (XHR, da fetch keinen Upload-Fortschritt liefert). */
export function uploadWithProgress(url: string, file: Blob, onProgress: (fraction: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.withCredentials = true;
    if (csrfToken) xhr.setRequestHeader("X-CSRF-TOKEN", csrfToken);
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      let problem: { code?: string; title?: string } = {};
      try {
        problem = JSON.parse(xhr.responseText);
      } catch {
        // kein JSON
      }
      reject(new ApiError(xhr.status, problem.code ?? "upload_failed", problem.title ?? "Upload fehlgeschlagen.", xhr.status >= 500));
    };
    xhr.onerror = () => reject(new ApiError(0, "offline", "Upload unterbrochen – keine Verbindung.", true));
    xhr.send(file);
  });
}

export const errorText = (e: unknown): string =>
  e instanceof ApiError ? `${e.message}${e.code && e.status !== 0 ? ` (${e.code})` : ""}` : "Unerwarteter Fehler.";
